<template>
  <div>
    <br />

    <base-button
      v-if="isFirst"
      class="noprint"
      type="secondary"
      style="margin: 2px"
      v-on:click="openPrintDialog()"
      >{{ $t("print") }}</base-button
    >
    <br />
    <br />

    <div class="container ml-0" v-if="dataReady">
      <h3 :style="!isFirst ? 'page-break-before: always' : ''">
        {{ game.name }}
      </h3>
      <h5>
        {{ new Date(game.opened).toLocaleDateString(
        this.$i18n.locale) }}
      </h5>
      <br />

      <CombiClassicCharts
        :game="game"
        :selfEvaluations="selfEvaluations"
        :numberOfCases="numberOfCases"
        printMode="false"
        :scenario="scenario"
        :cases="cases"
        :key="key"
        :skipPageBreak="isFirst"
        :survey1Averages="survey1Averages"
        :survey2Averages="survey2Averages"
      />

      <br />
    </div>
    <div v-else class="spinner-border" role="status"></div>
  </div>
</template>

<script>
import {
  getGameByGameCode,
  getScenario,
  getSelfEvaluationsByGame,
  getCases,
  getAndCommitScenario,
  getAndCommitCases,
  getSurveyAverage,
} from "@/services/APIService";

import CombiClassicCharts from "@/components/charts/CombiClassicCharts.vue";

export default {
  props: ["gameCode", "isFirst"],
  components: {
    CombiClassicCharts,
  },
  data: function () {
    return {
      game: null,
      scenario: [],
      cases: [],
      rpsCaseChoices: [],
      rpsAnswers: [],
      dataReady: false,
      selfEvaluations: [],
      key: 0,
      survey1Averages: [],
      survey2Averages: [],
    };
  },
  watch: {
    getLocale: {
      handler(newData, oldData) {
        this.key++;
      },
    },
  },
  computed: {
    getLocale() {
      return this.$i18n.locale;
    },
    numberOfCases() {
      let highestCaseNumber = 1;
      for (let i = 0; i < this.cases.length; i++) {
        if (this.cases[i].case_number > highestCaseNumber) {
          highestCaseNumber = this.cases[i].case_number;
        }
      }

      return highestCaseNumber;
    },
    getCasesInCurrentLocale() {
      const cases = this.cases.filter(
        (aCase) => aCase.locale === this.$i18n.locale
      );

      if (cases.length) {
        return cases;
      } else {
        // Not found in i18n locale, just get the first one
        const locale = this.cases[0].locale;

        return this.cases.filter((aCase) => aCase.locale === locale);
      }
    },
  },
  methods: {
    async updateData() {
      await getGameByGameCode(this.gameCode).then((game) => {
        this.game = game;
      });

      // We need to push scenario and cases also to VueX so that we can intialize colors.

      if (this.isFirst) {
        await getAndCommitScenario(
          this.game.scenario,
          this.gameCode,
          this.$store.commit
        );

        await getAndCommitCases(
          this.game.scenario,
          this.gameCode,
          this.$store.commit
        );

        this.$store.dispatch({
          type: "initializeColors",
        });
      }

      await getScenario(this.game.scenario, this.gameCode).then((scenario) => {
        this.scenario = scenario.data;
      });

      await getCases(this.game.scenario).then((cases) => {
        this.cases = cases.data;
      });

      await getSelfEvaluationsByGame(this.game.id, this.gameCode).then(
        (selfEvaluations) => {
          this.selfEvaluations = selfEvaluations.data;
        }
      );

      const surveyPromises = [];

      if (this.game.enable_survey) {
        const cases = this.getCasesInCurrentLocale;

        for (let i = 0; i < cases.length; i++) {
          const aCase = cases[i];

          surveyPromises.push(
            getSurveyAverage(
              this.game.id,
              aCase.case,
              1,
              this.gameCode
            ).then((data) => {
              this.survey1Averages[aCase.case_number] = data.average.toFixed(1);
            })
          );

          surveyPromises.push(
            getSurveyAverage(
              this.game.id,
              aCase.case,
              2,
              this.gameCode
            ).then((data) => {
              this.survey2Averages[aCase.case_number] = data.average.toFixed(1);
            })
          );
        }
      }

      Promise.all(surveyPromises).then(() => {
       this.dataReady = true;
      })
    },
    openPrintDialog() {
      window.print();
    },
  },
  async created() {
    this.updateData();
  },
};
</script>

<style scoped>
@media print {
  .noprint {
    display: none !important;
  }
}
</style>


<i18n>
{
  "en": {
    "report": "Report",
    "name": "Name:",
    "date": "Date:",
    "reporthasbeenmailed": "A link to this report has been sent to you via email.",
    "print": "Print...",
    "excel": "Export to Excel",
    "ownanswers": "Your own answers",
    "allanswers": "All answers"
  },
  "fi": {
    "report": "Raportti",
    "name": "Nimi:",
    "date": "Päiväys:",
    "reporthasbeenmailed": "Linkki tähän raporttiin on lähetetty sinulle sähköpostilla.",
    "print": "Tulosta...",
    "excel": "Vie Exceliin",
    "ownanswers": "Omat vastauksesi",
    "allanswers": "Kaikkien vastaukset"
  },
  "sv": {
    "report": "Rapporterna",
    "name": "Namn:",
    "date": "Datum:",
    "reporthasbeenmailed": "En länk till denna rapport har skickats till dig via e-post.",
    "print": "Skriv ut...",
    "excel": "Exportera till Excel",
    "ownanswers": "Dina egna svar",
    "allanswers": "Alla svar"
  },
  "de": {
    "report": "Berichte",
    "name": "Name:",
    "date": "Datum:",
    "reporthasbeenmailed": "Ein Link zu diesem Bericht wurde Ihnen per E-Mail gesendet.",
    "print": "Drucken...",
    "excel": "In Excel exportieren",
    "ownanswers": "Ihre eigenen Antworten",
    "allanswers": "Alle Antworten"    
  },
  "ko": {
    "report": "결과보고서",
    "name": "이름:",
    "date": "날짜:",
    "reporthasbeenmailed": "개별 결과보고서를 확인할 수 있는 링크가 이메일로 전송되었습니다.",
    "print": "인쇄",
    "excel": "엑셀파일로 다운받기",
    "ownanswers": "나만의 답변",
    "allanswers": "모든 답변"      
  }
}
</i18n>
